import type { View } from "@novorender/api";
import type { ReadonlyVec2, ReadonlyVec3 } from "gl-matrix";
import { vec2, vec3, vec4 } from "gl-matrix";
import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import { getCameraDir } from "features/engine2D/utils";
import { selectCrossSectionClipping } from "features/orthoCam";
import { getSnapToPlaneParams } from "features/orthoCam/utils";
import type { RenderState } from "features/render";
import { CameraType, renderActions, selectCameraType, selectClippingPlanes, selectViewMode } from "features/render";
import { SECTION_PLANE_HIGHLIGHT_GREEN_RGB } from "features/sectionPlane/sectionPlaneColors";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { selectIsProcoreDesign } from "slices/explorer/selectors";
import { ViewMode } from "types/misc";

import { projectPlaneAnchorToScreen } from "./anchorProjection";

export function useClippingPlaneActions() {
    const dispatch = useAppDispatch();
    const cameraType = useAppSelector(selectCameraType);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const uom = useUom();
    const viewMode = useAppSelector(selectViewMode);
    const outlinesEnabled = useAppSelector(selectClippingPlanes).outlines;
    const clipping = useAppSelector(selectCrossSectionClipping);

    const snapToPlane = useCallback(
        (view: View, idx: number, anchorPos?: ReadonlyVec3) => {
            dispatch(
                renderActions.setCamera({
                    type: CameraType.Orthographic,
                    goTo: { ...getSnapToPlaneParams({ planeIdx: idx, view, anchorPos }), far: clipping },
                }),
            );
        },
        [dispatch, clipping],
    );

    const swapCamera = useCallback(
        (view: View, planes: RenderState["clipping"]["planes"], idx: number) => {
            const isOrtho = view.renderState.camera.kind === "orthographic";
            const plane = planes.length > idx ? planes[idx] : undefined;
            const planeDir =
                plane && vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]);
            const isAlignedWithCamera =
                planeDir && vec3.dot(planeDir, getCameraDir(view.renderState.camera.rotation)) < -0.999;

            if (isOrtho && !plane) {
                dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
            } else if (isOrtho && isAlignedWithCamera) {
                dispatch(
                    renderActions.setCamera({
                        type: CameraType.Pinhole,
                        goTo: {
                            position: vec3.scaleAndAdd(vec3.create(), view.renderState.camera.position, planeDir, 15),
                            rotation: view.renderState.camera.rotation,
                        },
                    }),
                );
                // if camera is not aligned - just align with it and stay in ortho
            } else {
                if (viewMode === ViewMode.Default) {
                    dispatch(renderActions.setViewMode(ViewMode.CrossSection));
                }
                snapToPlane(view, idx, planes[idx].anchorPos);

                // Turn on outlines for the current plane and turn off for the rest
                if (!planes[idx].outline.enabled) {
                    dispatch(
                        renderActions.setClippingPlanes({
                            planes: planes.map((p, i) => ({ ...p, outline: { ...p.outline, enabled: i === idx } })),
                        }),
                    );
                }
            }
        },
        [dispatch, snapToPlane, viewMode],
    );

    const movePlanes = useCallback(
        (view: View, planes: RenderState["clipping"]["planes"], idxList: number[]) => {
            const originalCamera = view.renderState.camera;
            const cameraDir = getCameraDir(view.renderState.camera.rotation);

            const initCanvas = document.getElementById(ELEMENT_IDS.mainCanvas) as HTMLCanvasElement | null;
            const initCanvasWidth = initCanvas?.clientWidth ?? 0;
            const initCanvasHeight = initCanvas?.clientHeight ?? 0;

            const editedPlanes = planes
                .map((plane, idx) => ({ plane, idx }))
                .filter(({ idx }) => idxList.includes(idx))
                .map(({ plane, idx }) => {
                    const originalAnchorPos = plane.anchorPos;
                    const originalAnchorPos2d = originalAnchorPos
                        ? projectPlaneAnchorToScreen(view, originalAnchorPos, {
                              canvasWidth: initCanvasWidth,
                              canvasHeight: initCanvasHeight,
                              clampToCanvas: true,
                          })
                        : undefined;
                    const originalNormalOffset = plane.normalOffset;
                    const normal = vec3.fromValues(
                        originalNormalOffset[0],
                        originalNormalOffset[1],
                        originalNormalOffset[2],
                    );
                    const isParallelToGround = Math.abs(vec3.dot(normal, vec3.fromValues(0, 0, 1))) > 0.999;
                    const isParallelToCamera = Math.abs(vec3.dot(normal, cameraDir)) > 0.999;
                    const normalOffset = originalNormalOffset;
                    const anchorPos = originalAnchorPos;

                    const anchor = document.querySelector(`#${getClippingPlaneAnchorId(idx)}`) as
                        | HTMLElement
                        | undefined;
                    const offsetNode = anchor?.querySelector(`[data-offset]`) as HTMLElement;

                    return {
                        idx,
                        originalAnchorPos,
                        originalAnchorPos2d,
                        originalNormalOffset,
                        normal,
                        isParallelToGround,
                        isParallelToCamera,
                        normalOffset,
                        anchorPos,
                        anchor,
                        offsetNode,
                    };
                });

            const moveCameraToPlane = (diff: number) => {
                if (originalCamera.kind === "orthographic" && originalCamera.rotation && originalCamera.position) {
                    const pos = vec3.clone(originalCamera.position);
                    const dir = vec3.fromValues(0, 0, 1);
                    vec3.transformQuat(dir, dir, originalCamera.rotation);
                    vec3.scaleAndAdd(pos, pos, dir, diff);
                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: {
                                position: pos,
                                rotation: view.renderState.camera.rotation,
                                far: view.renderState.camera.far,
                            },
                        }),
                    );
                }
            };

            return {
                update(newValues: number[]) {
                    let movedCameraToPlane = false;
                    const canvas = document.getElementById(ELEMENT_IDS.mainCanvas) as HTMLCanvasElement | null;
                    const canvasWidth = canvas?.clientWidth ?? 0;
                    const canvasHeight = canvas?.clientHeight ?? 0;

                    editedPlanes.forEach((editedPlane, idx) => {
                        const {
                            originalNormalOffset,
                            isParallelToCamera,
                            anchor,
                            originalAnchorPos,
                            originalAnchorPos2d,
                            offsetNode,
                            isParallelToGround,
                        } = editedPlane;
                        const newValue = newValues[idx];
                        editedPlane.normalOffset = vec4.clone(editedPlane.originalNormalOffset);
                        const diff = editedPlane.originalNormalOffset[3] - newValue;
                        const { normalOffset } = editedPlane;
                        normalOffset[3] = newValue;

                        if (cameraType === CameraType.Orthographic && isParallelToCamera && !movedCameraToPlane) {
                            dispatch(renderActions.setClippingInEdit(true));
                            moveCameraToPlane(-diff);
                            movedCameraToPlane = true;
                        }

                        if (anchor && originalAnchorPos) {
                            editedPlane.anchorPos = vec3.scaleAndAdd(
                                vec3.create(),
                                originalAnchorPos,
                                vec3.fromValues(normalOffset[0], normalOffset[1], normalOffset[2]),
                                newValue - originalNormalOffset[3],
                            );

                            let pos2d: ReadonlyVec2 | undefined;
                            if (view.renderState.camera.kind === "orthographic" && isParallelToCamera) {
                                if (originalAnchorPos2d) {
                                    pos2d = vec2.clone(originalAnchorPos2d);
                                    pos2d[1] += diff;
                                }
                            } else {
                                pos2d = editedPlane.anchorPos
                                    ? projectPlaneAnchorToScreen(view, editedPlane.anchorPos, {
                                          canvasWidth,
                                          canvasHeight,
                                          clampToCanvas: true,
                                      })
                                    : undefined;
                            }

                            if (pos2d) {
                                anchor.style.setProperty("left", `${pos2d[0]}px`);
                                anchor.style.setProperty("top", `${pos2d[1]}px`);
                            }

                            if (!isProcoreDesign && offsetNode) {
                                offsetNode.style.setProperty("display", "block");
                                let text = `${diff > 0 ? "+" : ""}${convertAndFormat(uom, diff, UnitType.length)}`;
                                if (isParallelToGround && editedPlane.anchorPos) {
                                    text += ` (alt: ${convertAndFormat(uom, editedPlane.anchorPos[2], UnitType.length)})`;
                                }
                                offsetNode.innerText = text;
                            }
                        }
                    });

                    view.modifyRenderState({
                        clipping: {
                            planes: planes.map((p, i) => {
                                const editedPlane = editedPlanes.find((p2) => p2.idx === i);
                                if (editedPlane) {
                                    const { normalOffset, isParallelToCamera } = editedPlane;

                                    return {
                                        ...p,
                                        outline: isProcoreDesign
                                            ? {
                                                  enabled: true,
                                                  color: SECTION_PLANE_HIGHLIGHT_GREEN_RGB,
                                                  lineColor: SECTION_PLANE_HIGHLIGHT_GREEN_RGB,
                                              }
                                            : {
                                                  enabled: outlinesEnabled && p.outline.enabled,
                                                  lineColor: p.outline.color,
                                              },
                                        normalOffset,
                                        color:
                                            (cameraType === CameraType.Orthographic && isParallelToCamera) ||
                                            !p.showPlane
                                                ? [0, 0, 0, 0]
                                                : p.color,
                                    };
                                } else if (outlinesEnabled && p.outline.enabled) {
                                    return { ...p, outline: { enabled: true, lineColor: p.outline.color } };
                                }
                                return p;
                            }),
                            gpuComputed: true,
                        },
                    });
                },
                finish(save: boolean) {
                    view.modifyRenderState({
                        outlines: { on: true },
                        clipping: { gpuComputed: false },
                    });
                    if (save) {
                        dispatch(
                            renderActions.setClippingPlanes({
                                planes: planes.map((p, i) => {
                                    const editedPlane = editedPlanes.find((p2) => p2.idx === i);
                                    if (editedPlane) {
                                        const { normalOffset, anchorPos } = editedPlane;
                                        return { ...p, normalOffset, anchorPos };
                                    }
                                    return p;
                                }),
                            }),
                        );
                    } else {
                        dispatch(
                            renderActions.setClippingPlanes({
                                planes: planes.slice(),
                            }),
                        );
                        moveCameraToPlane(0);
                    }

                    if (cameraType === CameraType.Orthographic) {
                        dispatch(renderActions.setClippingInEdit(false));
                    }

                    const canvas = document.getElementById(ELEMENT_IDS.mainCanvas) as HTMLCanvasElement | null;
                    const canvasWidth = canvas?.clientWidth ?? 0;
                    const canvasHeight = canvas?.clientHeight ?? 0;
                    for (const { offsetNode, anchor, anchorPos, originalAnchorPos2d } of editedPlanes) {
                        if (anchor && anchorPos) {
                            offsetNode?.style.setProperty("display", "none");
                            const pos2d = save
                                ? projectPlaneAnchorToScreen(view, anchorPos, {
                                      canvasWidth,
                                      canvasHeight,
                                      clampToCanvas: true,
                                  })
                                : originalAnchorPos2d;
                            if (pos2d) {
                                anchor.style.setProperty("left", `${pos2d[0]}px`);
                                anchor.style.setProperty("top", `${pos2d[1]}px`);
                            }
                        }
                    }
                },
            } as MovingPlaneControl;
        },
        [dispatch, cameraType, isProcoreDesign, uom, outlinesEnabled],
    );

    const deletePlane = useCallback(
        (view: View, planes: RenderState["clipping"]["planes"], idx: number) => {
            view.modifyRenderState({
                outlines: { on: true },
            });
            const newPlanes = planes
                .filter((_, i) => i !== idx)
                .map((p, i) => (i === 0 ? { ...p, outline: { enabled: true } } : p));

            dispatch(
                renderActions.setClippingPlanes({
                    planes: newPlanes,
                }),
            );
            if (cameraType === CameraType.Orthographic) {
                dispatch(renderActions.setClippingInEdit(false));
            }

            return newPlanes;
        },
        [dispatch, cameraType],
    );

    const toggleOutlines = useCallback(
        (planes: RenderState["clipping"]["planes"], idx: number, enabled: boolean) => {
            dispatch(
                renderActions.setClippingPlanes({
                    planes: planes.map((p, i) => (i === idx ? { ...p, outline: { enabled } } : p)),
                }),
            );
        },
        [dispatch],
    );

    const setShowPlane = useCallback(
        (planes: RenderState["clipping"]["planes"], idx: number, enabled: boolean) => {
            const newPlanes = [...planes];
            newPlanes[idx] = { ...newPlanes[idx], showPlane: enabled };

            dispatch(renderActions.setClippingPlanes({ planes: newPlanes }));
        },
        [dispatch],
    );

    const alignCamera = useCallback(
        (view: View, planes: RenderState["clipping"]["planes"], idx: number) => {
            const { position, rotation } = getSnapToPlaneParams({
                planeIdx: idx,
                view,
                anchorPos: planes[idx].anchorPos,
                offset: 20,
            });
            dispatch(
                renderActions.setCamera({
                    type: CameraType.Pinhole,
                    goTo: { position, rotation },
                }),
            );
        },
        [dispatch],
    );

    const flipPlane = useCallback(
        (idx: number) => {
            dispatch(renderActions.flipClippingPlane(idx));
        },
        [dispatch],
    );

    return { swapCamera, deletePlane, movePlanes, toggleOutlines, setShowPlane, flipPlane, alignCamera };
}

export interface MovingPlaneControl {
    update: (newValues: number[]) => void;
    finish: (save: boolean) => void;
}

export function getClippingPlaneAnchorId(index: number) {
    return `clipping-plane-anchor-${index}`;
}
